import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaImage,
  FaClock,
} from "react-icons/fa";
import {
  workshopService,
  Workshop,
  WorkshopDate,
  mediaService,
} from "../../services/firebase";
// Removed styled-components import - now using regular CSS classes
import "./Workshops.css";
import PrimaryButton from "../../components/PrimaryButton";
import LocationIcone from "../../assets/icone/LocationIcone";
import RefresIcon from "assets/icone/RefresIcon";
import RightArrow from "assets/icone/RightArrow";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { he } from "date-fns/locale";
import {
  formatDateIsraelShort,
  formatLocalDateToYYYYMMDD,
  formatTimeToHHmm,
  logWorkshopDatePickerSelection,
  parseLocalDateString,
  parseTimeString,
} from "../../utils/dateUtils";

interface WorkshopTimePickerProps {
  value: string;
  onChange: (time: string) => void;
  inputId: string;
}

const WorkshopTimePicker: React.FC<WorkshopTimePickerProps> = ({
  value,
  onChange,
  inputId,
}) => (
  <div className="workshops-time-picker-wrapper">
    <div className="workshops-time-picker-field">
      <DatePicker
        id={inputId}
        selected={parseTimeString(value)}
        onChange={(date) => onChange(date ? formatTimeToHHmm(date) : "")}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={5}
        timeCaption="שעה"
        dateFormat="HH:mm"
        locale={he}
        className="workshops-form-input-date workshops-form-input-time"
        popperClassName="workshops-time-picker-popper"
        popperPlacement="bottom-start"
        placeholderText="בחר שעה"
      />
      <FaClock className="workshops-time-picker-icon" aria-hidden="true" />
    </div>
    <input
      type="text"
      className="workshops-time-required-hidden"
      value={value}
      readOnly
      required
      tabIndex={-1}
      aria-hidden="true"
    />
  </div>
);

const AdminWorkshops: React.FC = () => {
  const { t } = useTranslation();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isEditDateModalOpen, setIsEditDateModalOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(
    null
  );
  const [editingDate, setEditingDate] = useState<WorkshopDate | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    titleRu: "",
    description: "",
    descriptionRu: "",
    location: "",
    locationRu: "",
    price: "",
    imageUrl: "",
  });
  const [dateFormData, setDateFormData] = useState({
    date: "",
    time: "",
    maxParticipants: "",
  });
  const [editDateFormData, setEditDateFormData] = useState({
    date: "",
    time: "",
    maxParticipants: "",
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorRuRef = useRef<HTMLDivElement>(null);
  const [orderValues, setOrderValues] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    fetchWorkshops();
  }, []);

  // Update editor content when form data changes
  useEffect(() => {
    if (editorRef.current && isModalOpen) {
      const currentContent = editorRef.current.innerHTML;
      const newContent =
        formData.description ||
        "<p>הכנס תיאור הסדנה... (תוכל להשתמש בכפתורים למעלה להדגשה ורשימות)</p>";

      if (currentContent !== newContent) {
        editorRef.current.innerHTML = newContent;
      }
    }
  }, [formData.description, isModalOpen]);

  // Update Russian editor content when form data changes
  useEffect(() => {
    if (editorRuRef.current && isModalOpen) {
      const currentContent = editorRuRef.current.innerHTML;
      const newContent =
        formData.descriptionRu ||
        "<p>Введите описание мастер-класса... (вы можете использовать кнопки выше для выделения и списков)</p>";

      if (currentContent !== newContent) {
        editorRuRef.current.innerHTML = newContent;
      }
    }
  }, [formData.descriptionRu, isModalOpen]);

  const fetchWorkshops = async () => {
    try {
      const data = (await workshopService.getAll()) as Workshop[];
      setWorkshops(data);
      // עדכון ערכי הסדר לפי המיקום הנוכחי
      const initialOrderValues: { [key: string]: number } = {};
      data.forEach((workshop, index) => {
        if (workshop.id) {
          initialOrderValues[workshop.id] = index + 1;
        }
      });
      setOrderValues(initialOrderValues);
    } catch (error) {
          }
  };

  const handleAddClick = () => {
    setEditingWorkshop(null);
    setFormData({
      title: "",
      titleRu: "",
      description: "",
      descriptionRu: "",
      location: "",
      locationRu: "",
      price: "",
      imageUrl: "",
    });
    clearImageSelection();
    setIsModalOpen(true);
  };

  const handleEditClick = (workshop: Workshop) => {
    setEditingWorkshop(workshop);
    setFormData({
      title: workshop.title,
      titleRu: workshop.titleRu || "",
      description: workshop.description,
      descriptionRu: workshop.descriptionRu || "",
      location: workshop.location,
      locationRu: workshop.locationRu || "",
      price: workshop.price.toString(),
      imageUrl: workshop.imageUrl || "",
    });
    // Clear selected image first
    setSelectedImage(null);
    // Set image preview if exists
    if (workshop.imageUrl) {
      setImagePreview(workshop.imageUrl);
    } else {
      setImagePreview("");
    }
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    if (window.confirm(t("admin.confirmDelete"))) {
      try {
        await workshopService.delete(id);
        fetchWorkshops();
      } catch (error) {
              }
    }
  };

  const handleManageDatesClick = (workshop: Workshop) => {
    setSelectedWorkshop(workshop);
    setIsDateModalOpen(true);
  };

  const handleAddDate = async () => {
    if (!selectedWorkshop) return;

    try {
      await workshopService.addWorkshopDate(selectedWorkshop.id!, {
        date: dateFormData.date,
        time: dateFormData.time,
        maxParticipants: parseInt(dateFormData.maxParticipants),
        isAvailable: true,
      });

      // Reset form and refresh
      setDateFormData({
        date: "",
        time: "",
        maxParticipants: "",
      });
      const updatedWorkshop = await workshopService.getById(
        selectedWorkshop.id!
      );
      setSelectedWorkshop(updatedWorkshop);
      fetchWorkshops();
      // setIsDateModalOpen(false);
    } catch (error) {
          }
  };

  const handleDeleteDate = async (dateId: string) => {
    if (!selectedWorkshop) return;

    if (window.confirm("האם אתה בטוח שברצונך למחוק תאריך זה?")) {
      try {
        await workshopService.deleteWorkshopDate(selectedWorkshop.id!, dateId);

        // Update the selected workshop with fresh data
        const updatedWorkshop = await workshopService.getById(
          selectedWorkshop.id!
        );
        setSelectedWorkshop(updatedWorkshop);

        fetchWorkshops();
      } catch (error) {
              }
    }
  };

  const handleEditDateClick = (date: WorkshopDate) => {
    setEditingDate(date);
    setEditDateFormData({
      date: date.date,
      time: date.time,
      maxParticipants: date.maxParticipants.toString(),
    });
    setIsEditDateModalOpen(true);
  };

  const handleEditDate = async () => {
    if (!selectedWorkshop || !editingDate) return;

    try {
      await workshopService.updateWorkshopDate(
        selectedWorkshop.id!,
        editingDate.id,
        {
          date: editDateFormData.date,
          time: editDateFormData.time,
          maxParticipants: parseInt(editDateFormData.maxParticipants),
        }
      );

      // Reset form and refresh
      setEditDateFormData({
        date: "",
        time: "",
        maxParticipants: "",
      });
      setEditingDate(null);

      // Update the selected workshop with fresh data
      const updatedWorkshop = await workshopService.getById(
        selectedWorkshop.id!
      );
      setSelectedWorkshop(updatedWorkshop);

      fetchWorkshops();
      setIsEditDateModalOpen(false);
    } catch (error) {
          }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async (): Promise<string> => {
    if (!selectedImage) return formData.imageUrl;

    try {
      setUploadingImage(true);
      const uploadedImage = await mediaService.upload(
        selectedImage,
        "workshop"
      );
      return uploadedImage.url;
    } catch (error) {
            throw new Error("שגיאה בהעלאת התמונה");
    } finally {
      setUploadingImage(false);
    }
  };

  const clearImageSelection = () => {
    setSelectedImage(null);
    setImagePreview("");
  };

  // פונקציה לטיפול בהדבקה - מסירה גודל פונט וצבעים אבל שומרת על bold ורשימות
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    const pastedText = clipboardData.getData("text/plain");
    const pastedHtml = clipboardData.getData("text/html");

    // אם יש בחירה, נמחק אותה
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    // אם יש HTML, נטפל בו וננקה אותו
    if (pastedHtml) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = pastedHtml;

      // נסיר את כל תכונות הפונט אבל נשמור על bold, italic, רשימות
      const cleanElement = (element: Element | Node): Node | null => {
        if (element.nodeType === Node.TEXT_NODE) {
          return document.createTextNode(element.textContent || "");
        }

        if (element.nodeType !== Node.ELEMENT_NODE) {
          return null;
        }

        const el = element as Element;
        const tagName = el.tagName.toLowerCase();

        // שמירה על תגים מותרים בלבד
        const allowedTags = [
          "strong",
          "b",
          "em",
          "i",
          "u",
          "ul",
          "ol",
          "li",
          "p",
          "br",
        ];

        if (allowedTags.includes(tagName)) {
          const newNode = document.createElement(tagName);

          // העתקת תוכן הילדים בלבד (ללא style או תכונות אחרות)
          Array.from(el.childNodes).forEach((child) => {
            const cleaned = cleanElement(child);
            if (cleaned) {
              newNode.appendChild(cleaned);
            }
          });

          return newNode;
        } else {
          // אם התג לא מותר, נשמור רק את הטקסט מהילדים
          const fragment = document.createDocumentFragment();
          Array.from(el.childNodes).forEach((child) => {
            const cleaned = cleanElement(child);
            if (cleaned) {
              fragment.appendChild(cleaned);
            }
          });
          return fragment.childNodes.length > 0 ? fragment : null;
        }
      };

      // ניצור fragment נקי
      const fragment = document.createDocumentFragment();
      Array.from(tempDiv.childNodes).forEach((child) => {
        const cleaned = cleanElement(child);
        if (cleaned) {
          if (cleaned.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
            const frag = cleaned as DocumentFragment;
            Array.from(frag.childNodes).forEach((node) => {
              fragment.appendChild(node);
            });
          } else {
            fragment.appendChild(cleaned);
          }
        }
      });

      // אם יש תוכן נקי, נדביק אותו
      if (fragment.childNodes.length > 0) {
        range.insertNode(fragment);
      } else {
        // אם לא, נשתמש בטקסט הפשוט
        const textNode = document.createTextNode(pastedText);
        range.insertNode(textNode);
      }
    } else {
      // אם אין HTML, נדביק את הטקסט הפשוט
      const textNode = document.createTextNode(pastedText);
      range.insertNode(textNode);
    }

    // נעביר את הקורסור לסוף התוכן שהודבק
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    // נעדכן את ה-formData
    const target = e.currentTarget;
    if (target) {
      const html = target.innerHTML;
      if (target === editorRef.current) {
        setFormData({ ...formData, description: html });
      } else if (target === editorRuRef.current) {
        setFormData({ ...formData, descriptionRu: html });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Upload image if selected
      let finalImageUrl = formData.imageUrl;
      if (selectedImage) {
        finalImageUrl = await handleImageUpload();
      }

      const workshopData = {
        ...formData,
        price: parseFloat(formData.price),
        dates: editingWorkshop?.dates || [],
        imageUrl: finalImageUrl,
      };

      if (editingWorkshop) {
        await workshopService.update(editingWorkshop.id!, workshopData);
      } else {
        await workshopService.create(workshopData);
      }

      // Reset form and image
      setFormData({
        title: "",
        titleRu: "",
        description: "",
        descriptionRu: "",
        location: "",
        locationRu: "",
        price: "",
        imageUrl: "",
      });
      clearImageSelection();
      setIsModalOpen(false);
      fetchWorkshops();
    } catch (error) {
            alert(error instanceof Error ? error.message : "שגיאה בשמירת הסדנה");
    }
  };

  const formatDate = formatDateIsraelShort;

  // פונקציה לעדכון מיקום סדנה לפי מספר
  const handleOrderChange = async (workshopId: string, newPosition: number) => {
    // בדיקת תקינות המספר
    if (
      newPosition < 1 ||
      newPosition > workshops.length ||
      !Number.isInteger(newPosition)
    ) {
      alert(`אנא הזן מספר בין 1 ל-${workshops.length}`);
      return;
    }

    const currentIndex = workshops.findIndex((w) => w.id === workshopId);
    if (currentIndex === -1) return;

    // המרה לאינדקס (0-based)
    const targetIndex = newPosition - 1;

    // אם המיקום לא השתנה, אין צורך לעדכן
    if (currentIndex === targetIndex) return;

    const newWorkshops = [...workshops];
    const [movedWorkshop] = newWorkshops.splice(currentIndex, 1);
    newWorkshops.splice(targetIndex, 0, movedWorkshop);

    // עדכון displayOrder
    const reorderMap = newWorkshops.map((w, index) => ({
      id: w.id!,
      displayOrder: index,
    }));

    try {
      await workshopService.reorderWorkshops(reorderMap);
      setWorkshops(newWorkshops);
      // עדכון ערכי הסדר לאחר השינוי
      const updatedOrderValues: { [key: string]: number } = {};
      newWorkshops.forEach((workshop, idx) => {
        if (workshop.id) {
          updatedOrderValues[workshop.id] = idx + 1;
        }
      });
      setOrderValues(updatedOrderValues);
    } catch (error) {
            alert("שגיאה בעדכון הסדר");
    }
  };

  return (
    <div className="workshops-page-container">
      <div className="workshops-header">
        <h1 className="workshops-title">{t("admin.workshops")}</h1>
        <PrimaryButton
          className="workshops-add-button"
          icon={<FaPlus />}
          onClick={handleAddClick}
        >
          {t("admin.addWorkshop")}
        </PrimaryButton>
      </div>

      <div className="workshops-grid">
        {workshops.map((workshop, index) => (
          <div className="workshop-card-admin" key={workshop.id}>
            {/* שדה סדר תצוגה */}
            <div className="workshop-order-controls">
              <label className="order-label" htmlFor={`order-${workshop.id}`}>
                מיקום:
              </label>
              <input
                key={`order-${workshop.id}`}
                id={`order-${workshop.id}`}
                type="number"
                className="order-input"
                min="1"
                max={workshops.length}
                value={orderValues[workshop.id!] ?? index + 1}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value);
                  // אם הערך ריק או תקין, נעדכן את ה-state
                  if (
                    value === "" ||
                    (numValue >= 1 && numValue <= workshops.length)
                  ) {
                    setOrderValues((prev) => ({
                      ...prev,
                      [workshop.id!]: value === "" ? index + 1 : numValue,
                    }));
                  }
                }}
                onBlur={(e) => {
                  const newPosition = parseInt(e.target.value);
                  // אם הערך תקין ושונה מהנוכחי, נעדכן
                  if (
                    newPosition &&
                    newPosition >= 1 &&
                    newPosition <= workshops.length &&
                    newPosition !== index + 1
                  ) {
                    handleOrderChange(workshop.id!, newPosition);
                  } else {
                    // אם הערך לא תקין, נחזיר לערך המקורי
                    setOrderValues((prev) => ({
                      ...prev,
                      [workshop.id!]: index + 1,
                    }));
                  }
                }}
                onKeyDown={(e) => {
                  // אם לוחצים Enter, נפעיל את onBlur
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                title={`הזן מספר בין 1 ל-${workshops.length} כדי לשנות את המיקום`}
                aria-label={`מיקום סדנה - נוכחי: ${index + 1}`}
              />
            </div>
            <img
              className="workshop-image-admin"
              src={workshop.imageUrl || "/images/workshop-default.jpg"}
              alt={workshop.title}
            />
            <div className="workshop-content-admin">
              <h3 className="workshop-title-admin">{workshop.title}</h3>

              <div className="workshop-location-admin">
                <LocationIcone />
                <span>{workshop.location}</span>
              </div>

              <div className="workshop-availability-price-admin">
                <div className="workshop-availability-admin">
                  {workshop.dates &&
                  workshop.dates.length > 0 &&
                  workshop.dates.some((date) => date.isAvailable) ? (
                    <>
                      <div className="availability-dot-admin available"></div>
                      <span className="availability-text-admin available">
                        {t("available")}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="availability-dot-admin unavailable"></div>
                      <span className="availability-text-admin unavailable">
                        {t("unavailable")}
                      </span>
                    </>
                  )}
                </div>

                <div className="workshop-price-admin">₪{workshop.price}</div>
              </div>

              <div className="workshop-actions-admin">
                <PrimaryButton
                  className="workshop-action-button-admin edit"
                  onClick={() => handleEditClick(workshop)}
                  fullWidth={true}
                  icon={<FaEdit />}
                  iconPosition="left"
                >
                  ערוך פרטי סדנה
                </PrimaryButton>
                <PrimaryButton
                  className="workshop-action-button-admin manage"
                  onClick={() => handleManageDatesClick(workshop)}
                  fullWidth={true}
                  icon={<FaCalendarAlt />}
                  iconPosition="left"
                >
                  ניהול תאריכי סדנה
                </PrimaryButton>
                <PrimaryButton
                  className="workshop-action-button-admin delete"
                  onClick={() => handleDeleteClick(workshop.id!)}
                  fullWidth={true}
                  icon={<FaTrash />}
                  iconPosition="left"
                >
                  מחק סדנה
                </PrimaryButton>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Workshop Modal */}
      {isModalOpen && (
        <div className="workshops-modal">
          <div className="workshops-modal-content">
            <h2>{editingWorkshop ? "ערוך סדנה" : "הוסף סדנה חדשה"}</h2>
            <form className="workshops-form" onSubmit={handleSubmit}>
              <div className="workshops-form-group">
                <label className="workshops-form-label">כותרת HE</label>
                <input
                  className="workshops-form-input"
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">כותרת RU</label>
                <input
                  className="workshops-form-input"
                  type="text"
                  value={formData.titleRu}
                  onChange={(e) =>
                    setFormData({ ...formData, titleRu: e.target.value })
                  }
                  required
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">תיאור HE</label>
                <div className="wysiwyg-editor-container-workshops">
                  <div className="wysiwyg-toolbar-workshops">
                    <button
                      type="button"
                      className="toolbar-button-workshops"
                      onClick={() => {
                        document.execCommand("bold", false);
                      }}
                      title="הדגש טקסט"
                    >
                      <strong>Bold</strong>
                    </button>
                    <button
                      type="button"
                      className="toolbar-button-workshops"
                      onClick={() => {
                        document.execCommand("insertUnorderedList", false);
                      }}
                      title="הוסף רשימה עם נקודות"
                    >
                      • רשימה
                    </button>
                  </div>
                  <div
                    ref={editorRef}
                    className="wysiwyg-editor-workshops"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      const html = e.currentTarget.innerHTML;
                      setFormData({ ...formData, description: html });
                    }}
                    onPaste={handlePaste}
                  />
                </div>
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">תיאור RU</label>
                <div className="wysiwyg-editor-container-workshops">
                  <div className="wysiwyg-toolbar-workshops">
                    <button
                      type="button"
                      className="toolbar-button-workshops"
                      onClick={() => {
                        document.execCommand("bold", false);
                      }}
                      title="הדגש טקסט"
                    >
                      <strong>Bold</strong>
                    </button>
                    <button
                      type="button"
                      className="toolbar-button-workshops"
                      onClick={() => {
                        document.execCommand("insertUnorderedList", false);
                      }}
                      title="הוסף רשימה עם נקודות"
                    >
                      • רשימה
                    </button>
                  </div>
                  <div
                    ref={editorRuRef}
                    className="wysiwyg-editor-workshops "
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      const html = e.currentTarget.innerHTML;
                      setFormData({ ...formData, descriptionRu: html });
                    }}
                    onPaste={handlePaste}
                  />
                </div>
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">מיקום הסדנה HE</label>
                <input
                  className="workshops-form-input"
                  type="text"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  required
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">מיקום הסדנה RU</label>
                <input
                  className="workshops-form-input"
                  type="text"
                  value={formData.locationRu}
                  onChange={(e) =>
                    setFormData({ ...formData, locationRu: e.target.value })
                  }
                  required
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">מחיר</label>
                <input
                  className="workshops-form-input"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  required
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">תמונה</label>
                <div className="image-upload-container">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="תמונה נבחרת" />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={clearImageSelection}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* File Input */}
                  <div className="file-input-container">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="file-input"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="file-input-label">
                      {selectedImage || imagePreview ? (
                        <RefresIcon />
                      ) : (
                        <FaPlus />
                      )}
                      {selectedImage || imagePreview
                        ? "שנה תמונה"
                        : "העלאת תמונה"}
                    </label>
                  </div>

                  {/* Upload Progress */}
                  {uploadingImage && (
                    <div className="upload-progress">
                      <FaImage className="upload-icon" />
                      מעלה תמונה...
                    </div>
                  )}
                </div>
              </div>
              <div className="workshops-modal-actions">
                <button
                  className="workshops-button-workshops primary"
                  type="submit"
                >
                  {editingWorkshop
                    ? "שמירת העריכה והעלאת הסדנה"
                    : "  הוסף סדנה חדשה"}
                </button>
                <button
                  className="workshops-button-workshops secondary"
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                >
                  יציאה ללא שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dates Management Modal */}
      {isDateModalOpen && (
        <div className="workshops-modal">
          <div className="workshops-modal-content">
            <h2>ניהול תאריכים - {selectedWorkshop?.title}</h2>

            {/* Add New Date Form */}
            <form
              className="workshops-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddDate();
              }}
            >
              <div className="workshops-form-group">
                <label className="workshops-form-label">תאריך</label>
                <DatePicker
                  selected={
                    dateFormData.date
                      ? parseLocalDateString(dateFormData.date)
                      : null
                  }
                  onChange={(date) => {
                    const formattedDate = date
                      ? formatLocalDateToYYYYMMDD(date)
                      : "";
                    logWorkshopDatePickerSelection("add", date, formattedDate);
                    setDateFormData({ ...dateFormData, date: formattedDate });
                  }}
                  dateFormat="dd/MM/yyyy"
                  locale={he}
                  className="workshops-form-input-date"
                  calendarStartDay={0}
                  placeholderText="בחר תאריך"
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label" htmlFor="add-workshop-time">
                  שעה
                </label>
                <WorkshopTimePicker
                  inputId="add-workshop-time"
                  value={dateFormData.time}
                  onChange={(time) =>
                    setDateFormData({ ...dateFormData, time })
                  }
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">
                  מספר מקסימלי של משתתפים
                </label>
                <input
                  className="workshops-form-input"
                  type="number"
                  value={dateFormData.maxParticipants}
                  onChange={(e) =>
                    setDateFormData({
                      ...dateFormData,
                      maxParticipants: e.target.value,
                    })
                  }
                  required
                  min="1"
                />
              </div>
              <PrimaryButton
                fullWidth={true}
                type="submit"
                icon={<FaPlus />}
                iconPosition="left"
                className="workshop-action-button-dates-add"
              >
                הוסף תאריך
              </PrimaryButton>
            </form>

            {/* Existing Dates List */}
            <div style={{ marginTop: "20px" }}>
              <h3>תאריכים קיימים לסדנה</h3>
              <div className="workshops-form-group-dates-list">
                {selectedWorkshop?.dates?.map((date) => (
                  <div key={date.id} className="workshops-form-group-dates">
                    <div className="workshops-form-group-dates-content">
                      <p className="workshops-form-group-dates-content-item">
                        <strong>תאריך:</strong> {formatDate(date.date)}
                      </p>
                      <p className="workshops-form-group-dates-content-item">
                        <strong>שעה:</strong> {date.time}
                      </p>
                      <p className="workshops-form-group-dates-content-item">
                        <strong>משתתפים:</strong> {date.currentParticipants}/
                        {date.maxParticipants}
                      </p>
                      <p
                        className="workshops-form-group-dates-content-item"
                        style={{ color: date.isAvailable ? "green" : "red" }}
                      >
                        <strong>סטטוס:</strong>{" "}
                        {date.isAvailable ? "פנוי" : "מלא"}
                      </p>
                    </div>
                    <div className="workshops-form-group-dates-actions">
                      <PrimaryButton
                        className="workshop-action-button-dates-edit"
                        onClick={() => handleEditDateClick(date)}
                        fullWidth={true}
                      >
                        <FaEdit />
                        ערוך תאריך קיים
                      </PrimaryButton>
                      <PrimaryButton
                        className="workshop-action-button-dates-delete"
                        onClick={() => handleDeleteDate(date.id)}
                        fullWidth={true}
                      >
                        <FaTrash />
                        מחק תאריך קיים
                      </PrimaryButton>
                    </div>
                  </div>
                ))}
                {(!selectedWorkshop?.dates ||
                  selectedWorkshop.dates.length === 0) && (
                  <p>אין תאריכים זמינים</p>
                )}
              </div>
            </div>

            <div className="workshops-modal-actions-dates">
              <PrimaryButton
                className="workshops-button-workshops close-window"
                onClick={() => setIsDateModalOpen(false)}
              >
                סגירת חלון
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Edit Date Modal */}
      {isEditDateModalOpen && (
        <div className="workshops-modal">
          <div className="workshops-modal-content-edit-date">
            <button
              className="back-button-modal"
              onClick={() => setIsEditDateModalOpen(false)}
            >
              <span className="back-button-modal-text">
                <RightArrow />
                {t("back")}
              </span>
            </button>
            <h2>ערוך תאריך קיים</h2>

            <form
              className="workshops-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleEditDate();
              }}
            >
              <div className="workshops-form-group">
                <label className="workshops-form-label">תאריך</label>
                <DatePicker
                  selected={
                    editDateFormData.date
                      ? parseLocalDateString(editDateFormData.date)
                      : null
                  }
                  onChange={(date) => {
                    const formattedDate = date
                      ? formatLocalDateToYYYYMMDD(date)
                      : "";
                    logWorkshopDatePickerSelection("edit", date, formattedDate);
                    setEditDateFormData({
                      ...editDateFormData,
                      date: formattedDate,
                    });
                  }}
                  dateFormat="dd/MM/yyyy"
                  locale={he}
                  className="workshops-form-input-date"
                  calendarStartDay={0}
                  placeholderText="בחר תאריך"
                />
              </div>
              <div className="workshops-form-group">
                <label
                  className="workshops-form-label"
                  htmlFor="edit-workshop-time"
                >
                  שעה
                </label>
                <WorkshopTimePicker
                  inputId="edit-workshop-time"
                  value={editDateFormData.time}
                  onChange={(time) =>
                    setEditDateFormData({
                      ...editDateFormData,
                      time,
                    })
                  }
                />
              </div>
              <div className="workshops-form-group">
                <label className="workshops-form-label">
                  מספר מקסימלי של משתתפים
                </label>
                <input
                  className="workshops-form-input"
                  type="number"
                  value={editDateFormData.maxParticipants}
                  onChange={(e) =>
                    setEditDateFormData({
                      ...editDateFormData,
                      maxParticipants: e.target.value,
                    })
                  }
                  required
                  min="1"
                />
              </div>
              <div className="workshops-modal-actions">
                <button className="workshops-button update-date" type="submit">
                  עדכן תאריך
                </button>
                <button
                  className="workshops-button exit-without-changes"
                  type="button"
                  onClick={() => setIsEditDateModalOpen(false)}
                >
                  יציאה ללא שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWorkshops;
